import { useEffect, useId, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { createPortal } from 'react-dom';
import { Check, FileJson, Link2, X } from 'lucide-react';
import { inferInputSchema, parseBoundedJson, parseJsonObject, sourcePointers } from '../../services/inboundSchemasApi';
import { SchemaMappingBlock, SchemaMappingTargetActions, SchemaMappingJoinedSources, SchemaMappingSeparatorEditor,
  type SchemaMappingBlockTarget } from './SchemaMappingWizard';
import { mappingTargetState, transitionMappingTarget, type MappingTargetStateInput, type MappingTargetDraft } from './schemaMappingState';
import './InboundSchemaFieldMapper.css';

export interface MappingRow {
  id: string;
  output: string;
  mode: 'source' | 'value' | 'text' | 'sources';
  sources?: string[];
  combine?: 'array' | 'join';
  separator?: string;
  input: string;
  type: string;
  fallback: string;
  required: boolean;
  values: string;
}

type Field = { type: string; types: string[]; required: boolean; supported: boolean };
const dragType = 'application/x-zurvit-schema-pointer';
const supportedTypes = new Set(['string', 'integer', 'number', 'boolean', 'object', 'array', 'null']);
const declaredTypes = (type: unknown): string[] => {
  const types = typeof type === 'string' ? [type] : Array.isArray(type) ? type : [];
  return types.length && types.every(value => typeof value === 'string' && supportedTypes.has(value))
    && new Set(types).size === types.length ? types as string[] : [];
};

export function fieldAt(schema: Record<string, unknown>, pointer: string): Field | undefined {
  let node: unknown = schema;
  let required = false;
  let supported = true;
  const resolve = (value: unknown, checkComposition = true): Record<string, unknown> | undefined => {
    for (let depth = 0; depth < 24; depth++) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
      const record = value as Record<string, unknown>;
      if (checkComposition && ['allOf', 'anyOf', 'oneOf', 'not', 'if', 'then', 'else'].some(key => key in record)) supported = false;
      if (typeof record.$ref !== 'string') return record;
      if (Object.keys(record).length !== 1) supported = false;
      if (!record.$ref.startsWith('#/')) return undefined;
      value = record.$ref.slice(2).split('/').reduce<unknown>((parent, part) => parent && typeof parent === 'object'
        ? (parent as Record<string, unknown>)[part.replace(/~1/g, '/').replace(/~0/g, '~')] : undefined, schema);
    }
    return undefined;
  };
  for (const segment of pointer === '' ? [] : pointer.slice(1).split('/')) {
    const parent = resolve(node, false);
    const key = segment.replace(/~1/g, '/').replace(/~0/g, '~');
    if (!declaredTypes(parent?.type).includes('object')) supported = false;
    if (!parent || !parent.properties || typeof parent.properties !== 'object' || Array.isArray(parent.properties)
      || !Object.prototype.hasOwnProperty.call(parent.properties, key)) return undefined;
    required = Array.isArray(parent.required) && parent.required.includes(key);
    node = (parent.properties as Record<string, unknown>)[key];
  }
  const field = resolve(node);
  return field ? { type: typeof field.type === 'string' ? field.type : Array.isArray(field.type) ? field.type.join(' | ') : 'unspecified',
    types: declaredTypes(field.type), required, supported: supported && declaredTypes(field.type).length > 0 } : undefined;
}

const runtimeFields = new Set(['title', 'description', 'severity', 'service', 'source', 'sourceEventId', 'correlationId', 'occurredUtc', 'status', 'externalLink']);

export function runtimeBindingsAt(bindingsJson: string, pointer: string): string[] {
  return Object.entries(parseJsonObject(bindingsJson)).filter(([name, rule]) => runtimeFields.has(name)
    && rule !== null && typeof rule === 'object' && !Array.isArray(rule)
    && (rule as Record<string, unknown>).source === pointer).map(([name]) => name);
}

export function collectionTargetError(outputSchema: string, bindingsJson: string, pointer: string): string {
  let field: Field | undefined;
  let names: string[];
  try { field = fieldAt(parseJsonObject(outputSchema), pointer); names = runtimeBindingsAt(bindingsJson, pointer); }
  catch { return 'Invalid saved output or runtime bindings JSON. Review Normalised Output.'; }
  if (names.length) return `${pointer} is ${field?.type === 'array' || field?.type === 'object' || field?.type === 'integer' ? 'an' : 'a'} ${field?.type ?? 'type-unspecified'} field used by the ${names.join(', ')} runtime binding. Keep it a string. To collect values, add a separate array field such as /descriptionParts to Normalised Output, save output settings, then map both inputs there.`;
  if (!field?.supported || field.type !== 'array') return `${pointer}: the saved output field needs array type before adding mappings. Edit this property in Normalised Output to declare type array and items with a type matching your source values, then save output settings. Remove incompatible string restrictions such as minLength or string enum values; they cannot be kept on the array field.`;
  return '';
}

export function joinTargetError(outputSchema: string, pointer: string): string {
  try {
    const field = fieldAt(parseJsonObject(outputSchema), pointer);
    return field?.supported && field.type === 'string' ? '' : `${pointer}: Join text requires a saved string output field.`;
  } catch { return 'Invalid saved output JSON. Review Normalised Output.'; }
}

export function aggregateDefaultError(row: MappingRow, combine: 'array' | 'join'): string {
  if (!row.fallback.trim()) return '';
  try {
    const fallback = parseBoundedJson(row.fallback);
    if (combine === 'join' ? typeof fallback === 'string' : Array.isArray(fallback)) return '';
  } catch { return 'Review invalid Default JSON before aggregating. Existing settings are unchanged.'; }
  return `Review Default JSON: ${combine === 'join' ? 'Join text needs a string' : 'Collect array needs an array'}. Existing settings are unchanged.`;
}

export function staticTargetError(outputSchema: string, pointer: string): string {
  try {
    const field = fieldAt(parseJsonObject(outputSchema), pointer);
    return field?.supported && field.types.includes('string') ? '' : `${pointer}: Static text requires an output field admitting string type.`;
  } catch { return 'Invalid saved output JSON. Review Normalised Output.'; }
}

export function InboundSchemaFieldMapper({ rows, mappingReady, inputSchemaJson, example, outputSchema, savedRuntimeBindingsJson = '{}', sourceSearchContainer, onEditOutput, onMap, onUnmap, onReorderSources, onSetSeparator, onSetJoinMode, onSetStaticText, onSetRequired, onPendingRequiredChange, draftTargets, onDraftTargetsChange }: {
  rows: MappingRow[];
  mappingReady: boolean;
  inputSchemaJson: string;
  example: string;
  outputSchema: string;
  savedRuntimeBindingsJson?: string;
  sourceSearchContainer?: HTMLElement | null;
  onEditOutput?: () => void;
  onMap: (source: string, target: string, operation?: 'add' | 'replace') => void;
  onUnmap: (target: string, source?: string) => void;
  onReorderSources?: (target: string, sources: string[]) => void;
  onSetSeparator?: (target: string, separator: string) => void;
  onSetJoinMode?: (target: string, joined: boolean) => void;
  onSetStaticText?: (target: string, text: string) => void;
  onSetRequired?: (target: string, required: boolean) => void;
  onPendingRequiredChange?: (targets: string[]) => void;
  draftTargets?: Record<string, MappingTargetDraft>;
  onDraftTargetsChange?: Dispatch<SetStateAction<Record<string, MappingTargetDraft>>>;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [localTargetDrafts, setLocalTargetDrafts] = useState<Record<string, MappingTargetDraft>>({});
  const targetDrafts = draftTargets ?? localTargetDrafts;
  const setTargetDrafts = onDraftTargetsChange ?? setLocalTargetDrafts;
  const [pending, setPending] = useState<{ source: string; target: string; signature: string } | null>(null);
  const [staticEditor, setStaticEditor] = useState<{ target: string; text: string; signature: string | undefined } | null>(null);
  const dragging = useRef<string | null>(null);
  const confirmation = useRef<HTMLDivElement>(null);
  const cancelConfirmation = useRef<HTMLButtonElement>(null);
  const confirmationTitle = useId();
  const confirmationDescription = useId();
  const confirmationOpen = pending !== null;
  useEffect(() => {
    if (!confirmationOpen) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cancelConfirmation.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [confirmationOpen]);
  let input: Record<string, unknown> = {};
  let output: Record<string, unknown> = {};
  let sample: Record<string, unknown> = {};
  let invalid = false;
  try {
    input = parseJsonObject(inputSchemaJson);
    output = parseJsonObject(outputSchema);
    parseJsonObject(savedRuntimeBindingsJson);
    if (example.trim()) sample = inferInputSchema(example);
  } catch { invalid = true; }
  const sources = invalid ? [] : sourcePointers(inputSchemaJson, example).filter(pointer => pointer !== '');
  const declared = invalid ? [] : sourcePointers(outputSchema, '').filter(pointer => pointer !== '' && fieldAt(output, pointer));
  const supportedTargets = declared.filter(pointer => fieldAt(output, pointer)?.supported && pointer.length <= 1024 && pointer.split('/').length <= 33);
  const targets = Array.from(new Set([...declared, ...rows.map(row => row.output)]));
  const enabled = mappingReady && !invalid;
  const selectedSource = selected !== null && sources.includes(selected) ? selected : null;
  const ruleFor = (target: string) => rows.filter(row => row.output === target);
  const stateInput = (target: string): MappingTargetStateInput => {
    const row = ruleFor(target)[0];
    const draftState = targetDrafts[target];
    const supportsText = supportedTargets.includes(target) && !!fieldAt(output, target)?.types.includes('string') && target !== '/rawPayload';
    return { selected: !!row || !!draftState?.selected, required: draftState?.required ?? row?.required ?? false,
      disabled: !enabled,
      value: row ? row.mode === 'source' ? { kind: 'source', source: row.input || '(root input)' }
        : row.mode === 'sources' ? { kind: 'join', sources: row.sources ?? [] } : { kind: 'static' }
        : draftState?.joined ? { kind: 'join', sources: [] } : null,
      supportsJoin: supportsText && fieldAt(output, target)?.type === 'string', supportsStatic: supportsText };
  };
  const pendingRequiredJson = JSON.stringify(targets.filter(target => mappingTargetState(stateInput(target)).state === 'required-unmapped'));
  useEffect(() => { onPendingRequiredChange?.(JSON.parse(pendingRequiredJson) as string[]); }, [pendingRequiredJson, onPendingRequiredChange]);
  useEffect(() => () => onPendingRequiredChange?.([]), [onPendingRequiredChange]);
  const commitMapping = (source: string, target: string, operation?: 'add' | 'replace') => {
    const controls = mappingTargetState(stateInput(target));
    if (operation) onMap(source, target, operation);
    else onMap(source, target);
    if (controls.required) onSetRequired?.(target, true);
    if (targetDrafts[target]?.joined && !ruleFor(target).length) onSetJoinMode?.(target, true);
    setTargetDrafts(current => { const next = { ...current }; delete next[target]; return next; });
    setSelected(null); setPending(null);
  };
  const removeMapping = (target: string, source?: string) => {
    const input = stateInput(target);
    const next = transitionMappingTarget({ ...input, required: input.required && supportedTargets.includes(target) && target !== '/rawPayload' }, { type: source === undefined ? 'remove' : 'remove-source' });
    setTargetDrafts(current => ({ ...current, [target]: { selected: next.selected, required: next.required,
      joined: source !== undefined && ruleFor(target)[0]?.combine === 'join' } }));
    if (source === undefined) onUnmap(target);
    else onUnmap(target, source);
    setPending(null); setStaticEditor(null); setSelected(null); dragging.current = null;
  };
  const staticAllowed = (target: string) => enabled && !!onSetStaticText && target !== '/rawPayload'
    && supportedTargets.includes(target) && !!fieldAt(output, target)?.types.includes('string')
    && ruleFor(target).length <= 1 && (ruleFor(target).length === 1 || rows.length < 200);
  const staticValid = staticEditor && staticAllowed(staticEditor.target)
    && JSON.stringify(ruleFor(staticEditor.target)[0]) === staticEditor.signature;
  const valid = (source: string, target: string) => enabled && !mappingTargetState(stateInput(target)).mappingDisabled && sources.includes(source) && supportedTargets.includes(target)
    && ruleFor(target).length <= 1 && (ruleFor(target).length === 1 || rows.length < 200);
  const requestMap = (source: string, target: string) => {
    if (!valid(source, target)) return;
    setStaticEditor(null);
    const existing = ruleFor(target)[0];
    if (existing && mappingTargetState(stateInput(target)).mapped) setPending({ source, target, signature: JSON.stringify(existing) });
    else commitMapping(source, target);
  };
  const pendingValid = pending && valid(pending.source, pending.target)
    && JSON.stringify(ruleFor(pending.target)[0]) === pending.signature;
  const joinSourceError = (pointer: string) => {
    const declared = fieldAt(input, pointer);
    if (!declared) {
      return fieldAt(sample, pointer)
        ? `${pointer} is available from Example payload but is not declared in Input JSON Schema. Declare it there before joining sources.`
        : `${pointer} is not declared in Input JSON Schema. Declare it as a scalar field before joining sources.`;
    }
    if (!declared.supported)
      return `${pointer} is declared as ${declared.type}, but its schema shape is unsupported for Join text; use a supported scalar type.`;
    if (!['string', 'integer', 'number', 'boolean'].includes(declared.type))
      return `${pointer} is declared as ${declared.type}; Join text requires a string, number, integer or boolean scalar source.`;
    return '';
  };
  const addBlocked = (source: string, target: string) => {
    const row = ruleFor(target)[0];
    if (target === '/rawPayload' || source === '' || (row?.mode === 'source' && row.input === '')) return 'Raw root input cannot be collected.';
    const combine = fieldAt(output, target)?.type === 'string' ? 'join' : 'array';
    const setupError = combine === 'join' ? joinTargetError(outputSchema, target) : collectionTargetError(outputSchema, savedRuntimeBindingsJson, target);
    if (setupError) return setupError;
    if (row?.mode === 'sources' && (row.combine ?? 'array') !== combine) return 'Choose Join text or Collect array explicitly in the rule before adding sources.';
    if (row?.mode === 'value' || row?.mode === 'text') return 'Literal mappings cannot be collected. Replace the mapping instead.';
    if (row?.type || row?.values.trim()) return 'Remove conversion and values first before collecting sources. Existing settings are unchanged.';
    if (row && aggregateDefaultError(row, combine)) return aggregateDefaultError(row, combine);
    const pointers = row?.mode === 'sources' ? row.sources ?? [] : row ? [row.input] : [];
    if (combine === 'join') {
      const sourceError = [...pointers, source].map(joinSourceError).find(Boolean);
      if (sourceError) return sourceError;
    }
    if (pointers.includes(source)) return 'This source is already mapped to this array.';
    if (pointers.length >= 32) return 'An aggregate mapping supports at most 32 sources.';
    return '';
  };
  const metadata = (field: Field | undefined) => field ? `${field.type}${field.required ? ' / required' : ''}` : 'type unspecified';
  const sourceField = (pointer: string) => fieldAt(input, pointer) ?? fieldAt(sample, pointer);
  const setupSources = pending ? [pending.source, ...(ruleFor(pending.target)[0]?.mode === 'sources'
    ? ruleFor(pending.target)[0].sources ?? [] : [ruleFor(pending.target)[0]?.input ?? ''])] : [];
  const setupTypes = Array.from(new Set(setupSources.map(pointer => sourceField(pointer)?.type)
    .filter((type): type is string => !!type && ['string', 'integer', 'number', 'boolean', 'object', 'array', 'null'].includes(type))));
  const mismatch = (source: string, target: string, rule?: MappingRow) => {
    if (rule?.values.trim() && !rule.type) return false;
    const from = rule?.type ? [rule.type === 'datetime' ? 'string' : rule.type] : sourceField(source)?.types ?? [];
    const to = fieldAt(output, target)?.types ?? [];
    return from.length > 0 && to.length > 0 && from.some(type => !to.includes(type) && !(type === 'integer' && to.includes('number')));
  };
  const sourceItems = sources.map(source => {
    const used = rows.filter(row => row.mode === 'source' ? row.input === source : row.mode === 'sources' && row.sources?.includes(source));
    return { id: source, label: source, details: metadata(sourceField(source)), mappedTargets: used.map(row => row.output), searchText: metadata(sourceField(source)) };
  });
  const targetItems: SchemaMappingBlockTarget[] = targets.map(target => ({
    id: target,
    label: target || '(root)',
    details: fieldAt(output, target) ? `${fieldAt(output, target)!.type}${fieldAt(output, target)!.required ? ' · required by schema' : ''}` : 'type unspecified',
    note: !supportedTargets.includes(target) ? 'Outside supported declared output. Detach or edit the rule.' : undefined,
    state: mappingTargetState(stateInput(target)).state,
    className: 'outbound-action-mapped-cell',
    selectable: true,
    enabled: mappingTargetState(stateInput(target)).selected,
    requiredToggle: !!onSetRequired && supportedTargets.includes(target) && target !== '/rawPayload',
    requiredLabel: 'Require mapping',
    required: mappingTargetState(stateInput(target)).required,
    requiredDisabled: mappingTargetState(stateInput(target)).requiredDisabled,
  }));

  return <div className="inbound-field-mapper" aria-label="Visual field mapping">
    <p className="help-text">A type such as “string | null” allows text or JSON null. “Required by schema” means the output key must exist; “Require mapping” means this field must have an input mapping.</p>
    <SchemaMappingBlock ariaLabel="Inbound schema mapping" sources={sourceItems} targets={targetItems} dragType={dragType}
      selectedSource={selectedSource} disabled={!enabled} sourceHeading="Input fields" targetHeading="Output schema fields"
      sourceSearchLabel="Search input fields" targetTableLabel="Output field mappings"
      sourceSearchContainer={sourceSearchContainer}
      onSetTargetRequired={(target, required) => {
        const next = transitionMappingTarget(stateInput(target), { type: 'required', required });
        setTargetDrafts(current => ({ ...current, [target]: { selected: next.selected, required: next.required } }));
        if (ruleFor(target).length) {
          if (!next.preserveValue && !next.selected) onUnmap(target);
          else {
            onSetRequired?.(target, required);
            if (next.preserveValue) setTargetDrafts(current => { const result = { ...current }; delete result[target]; return result; });
          }
        }
      }}
      mappedHeading="Mapped Input field" sourceEmpty="No input fields available."
      targetEmpty="No declared output object-property fields available." targetTableClassName="inbound-schema-mapping-table outbound-action-mapping-table"
      actionsHeading="Mapping actions" actionsClassName="outbound-action-mapping-controls"
      onSetTargetEnabled={(target, checked) => {
        const next = transitionMappingTarget(stateInput(target), { type: 'select', selected: checked });
        setTargetDrafts(current => ({ ...current, [target]: { selected: next.selected, required: next.required } }));
        if (!next.selected) onUnmap(target);
        setPending(null); setStaticEditor(null); setSelected(null);
      }}
      onSelectSource={source => { setSelected(source); setPending(null); setStaticEditor(null); }}
      onDragStart={source => { dragging.current = source; }} onDragEnd={() => { dragging.current = null; }}
      canDrop={(source, target) => dragging.current === source && valid(source, target)}
      onDropSource={(source, target) => { requestMap(source, target); dragging.current = null; }}
      renderTarget={(targetItem, selected) => {
        const target = targetItem.id;
        const existing = ruleFor(target)[0];
        const connected = existing?.mode === 'source' ? existing.input : existing?.mode === 'sources' ? existing.sources?.[0] : null;
        const controls = mappingTargetState(stateInput(target));
        const warningSource = controls.selected ? selected ?? connected : null;
        return <>
          <button type="button" className="outbound-action-mapped-value" disabled={!enabled || selected === null || !valid(selected, target)}
            aria-label={selected === null ? `Map to ${target}` : `Map ${selected} to ${target}`}
            title={`Map selected source to ${target}`} onClick={() => { if (selected !== null) requestMap(selected, target); }}>
            <span className="outbound-action-mapped-icon"><Link2 size={16} aria-hidden="true" /></span><span className="inbound-field-mapper-connection">{controls.mapped && existing ? (existing.mode === 'source' ? (existing.input || '(root input)') : existing.mode === 'sources' ? existing.combine === 'join' ? 'Join text' : 'Collect values into an array' : existing.mode === 'text' ? <><small>Static text</small><span className="inbound-field-mapper-static-value">{existing.input}</span></> : `Literal ${existing.input}`) : controls.selected ? controls.joined ? 'Select or drag sources to join' : 'Select or drag an input' : 'Not selected'}</span>
          </button>
          {existing?.mode === 'sources' && <SchemaMappingJoinedSources label={`Sources for ${target}`}
            className="inbound-field-mapper-sources" sources={existing.sources ?? []}
            sourceLabel={source => source || '(root input)'} removeLabel={source => `Detach ${source || '(root input)'} from ${target}`}
            onRemove={source => removeMapping(target, source)}
            onReorder={onReorderSources ? sources => onReorderSources(target, sources) : undefined} />}
          {existing?.mode === 'sources' && !invalid && (existing.combine === 'join' ? joinTargetError(outputSchema, target) : collectionTargetError(outputSchema, savedRuntimeBindingsJson, target)) && <small role="alert" className="inbound-field-mapper-warning">{existing.combine === 'join' ? joinTargetError(outputSchema, target) : collectionTargetError(outputSchema, savedRuntimeBindingsJson, target)}</small>}
          {existing?.combine === 'join' && <SchemaMappingSeparatorEditor presetLabel={`Separator preset for ${target}`}
            valueLabel={`Join separator for ${target}`} value={existing.separator}
            onChange={separator => onSetSeparator?.(target, separator)} />}
          {existing?.combine === 'join' && <small>Joined values are converted to scalar text.</small>}
          {warningSource && !(existing?.combine === 'join' && ['string', 'integer', 'number', 'boolean'].includes(sourceField(warningSource)?.type ?? '')) && fieldAt(output, target)?.type !== 'array' && mismatch(warningSource, target, existing) && <small className="inbound-field-mapper-warning">Type mismatch. No automatic conversion.</small>}
        </>;
      }} renderActions={targetItem => {
        const target = targetItem.id;
        const existing = ruleFor(target)[0];
        const controls = mappingTargetState(stateInput(target));
        const joined = controls.joined;
        const joinSources = existing?.mode === 'sources' ? existing.sources ?? [] : existing?.mode === 'source' ? [existing.input] : [];
        const joinAllowed = !existing || existing.mode === 'value' || existing.mode === 'text' || (existing.mode === 'source' || existing.mode === 'sources')
          && !existing.type && !existing.values.trim() && joinSources.length > 0
          && joinSources.every(pointer => !!pointer && !joinSourceError(pointer))
          && !aggregateDefaultError(existing, 'join');
        return <SchemaMappingTargetActions controls={controls}
            joinLabel={`${joined ? 'Use single source' : 'Join text'} for ${target || '(root)'}`}
            joinTitle={joined ? 'Use the first source only' : 'Join compatible scalar sources with a separator'}
            joinBlocked={!joinAllowed || !onSetJoinMode}
            onJoin={() => {
              setTargetDrafts(current => ({ ...current, [target]: { selected: controls.selected, required: controls.required, joined: !joined } }));
              if (existing && (existing.mode === 'source' || existing.mode === 'sources') && joinSources.length) onSetJoinMode?.(target, !joined);
              else if (existing) onUnmap(target);
              setSelected(null); setPending(null); setStaticEditor(null); dragging.current = null;
            }}
          staticLabel={`Set static text for ${target || '(root)'}`}
          staticTitle={staticAllowed(target) ? `Set static text for ${target}` : 'Static text requires a declared string output field and room within the 200-rule limit.'}
          staticBlocked={!staticAllowed(target)}
          onStatic={() => { setStaticEditor({ target, text: existing?.mode === 'text' ? existing.input : '', signature: JSON.stringify(existing) }); setPending(null); setSelected(null); }}
          removeLabel={`Detach mapping for ${target || '(root)'}`} onRemove={() => removeMapping(target)} />;
      }} />
    {invalid && <p role="status">Invalid JSON. Field mapping is unavailable.</p>}
    {rows.length >= 200 && <p role="status">200-rule limit reached. Existing mappings can still be replaced.</p>}
    {staticEditor && <div className="inbound-field-mapper-confirm" role="group" aria-label="Static text editor">
      <label>Static text for {staticEditor.target}<textarea aria-label={`Static text for ${staticEditor.target}`} value={staticEditor.text}
        onChange={event => setStaticEditor({ ...staticEditor, text: event.target.value })} /></label>
      {staticEditor.signature && <p>{ruleFor(staticEditor.target)[0]?.mode === 'text'
        ? 'Existing static text modifiers remain unchanged; conversion and values may alter the resulting text.'
        : 'Replace the existing mapping with static text. Source, aggregate, conversion, default and values settings will be discarded. Required remains unchanged.'}</p>}
      {!staticValid && <p role="status">Mapping or output settings changed. Cancel and reopen the static text editor.</p>}
      <button type="button" disabled={!staticValid} onClick={() => {
        if (staticValid) {
          const required = mappingTargetState(stateInput(staticEditor.target)).required;
          onSetStaticText?.(staticEditor.target, staticEditor.text);
          if (required) onSetRequired?.(staticEditor.target, true);
          const target = staticEditor.target;
          setTargetDrafts(current => { const next = { ...current }; delete next[target]; return next; });
          setStaticEditor(null);
        }
      }}><Check size={16} aria-hidden="true" />{staticEditor.signature ? 'Replace with static text' : 'Apply static text'}</button>
      <button type="button" onClick={() => setStaticEditor(null)}><X size={16} aria-hidden="true" />Cancel</button>
    </div>}
    {pending && createPortal(<div className="inbound-mapping-confirm-overlay" onMouseDown={event => {
      if (event.target === event.currentTarget) setPending(null);
    }} onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setPending(null); }
      if (event.key === 'Tab') {
        event.stopPropagation();
        const controls = Array.from(confirmation.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), summary, [href]') ?? [])
          .filter(control => !control.closest('details:not([open])') || control.tagName === 'SUMMARY');
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }}>
      <div className="inbound-mapping-confirm-dialog" ref={confirmation} role="alertdialog" aria-modal="true"
        aria-label="Confirm mapping replacement" aria-labelledby={confirmationTitle} aria-describedby={confirmationDescription}>
      <h3 id={confirmationTitle}>Join or replace mapping</h3>
      <p id={confirmationDescription}>Replace mapping for {pending.target} with {pending.source}? Conversion, default, required and values remain unchanged; review their compatibility.</p>
      {!pendingValid && <p role="status">Mapping or output settings changed. Cancel and select the fields again.</p>}
      <div className="inbound-mapping-confirm-actions">
      <button type="button" disabled={!pendingValid || !!addBlocked(pending.source, pending.target)} onClick={() => {
        if (pendingValid && !addBlocked(pending.source, pending.target)) commitMapping(pending.source, pending.target, 'add');
      }}><Link2 size={16} aria-hidden="true" />Add mapping</button>
      <button type="button" disabled={!pendingValid} onClick={() => {
        if (pendingValid) commitMapping(pending.source, pending.target, 'replace');
      }}><Check size={16} aria-hidden="true" />Replace mapping</button>
      <button type="button" ref={cancelConfirmation} onClick={() => setPending(null)}><X size={16} aria-hidden="true" />Cancel</button>
      </div>
      {addBlocked(pending.source, pending.target) && <p role="status">{addBlocked(pending.source, pending.target)}</p>}
      {!invalid && fieldAt(output, pending.target)?.type !== 'string' && collectionTargetError(outputSchema, savedRuntimeBindingsJson, pending.target) && <>
        {onEditOutput && <button type="button" onClick={() => { setPending(null); onEditOutput(); }}><FileJson size={16} aria-hidden="true" />Review Normalised Output</button>}
        <details><summary>Array setup example</summary><pre>{JSON.stringify({ properties: { descriptionParts: { type: 'array', items: { type: setupTypes.length > 1 ? setupTypes : setupTypes[0] ?? 'string' } } } }, null, 2)}</pre>
          <pre>{JSON.stringify({ '/descriptionParts': { sources: ['/alertName', '/alertDescription'], combine: 'array' } }, null, 2)}</pre>
          <p>Use items types matching the inputs. Edit and save output settings first; this example does not change settings or bindings.</p>
        </details>
      </>}
      </div>
    </div>, document.body)}
  </div>;
}